import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, Row, Col, Table, Tag, Upload, Empty, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { ClientsApi } from 'api/api';
import { ClientDetailDto, ClientCashFlowDto, ClientType } from 'api/models';
import { Button } from 'components/buttons/buttons';
import { PageHeader } from 'components/page-headers/page-headers';
import { CardToolbox, Main, TableWrapper } from 'container/styled';
import FeatherIcon from 'feather-icons-react';
import openNotificationWithIcon from 'utility/notification';
import styled from 'styled-components';
import { formatCurrency, getCashFlowStatusColor, getCashFlowStatusText, getFileIcon } from 'utility/utility';
import { CustomTableStyleWrapper } from 'pages/style';
import { DocumentItem } from 'pages/unit-categories/styles';
import { DocumentGrid } from 'pages/unit-categories/styles';
import { hasPermission } from 'utility/accessibility/hasPermission';
import { ClientForm } from './ClientForm';

const clientsApi = new ClientsApi();

const ClientInfoItem = styled.div`
  padding: 8px;
  height: 100%;

  label {
    display: block;
    color: #8c8c8c;
    font-size: 12px;
    text-transform: uppercase;
    margin-bottom: 8px;
  }

  .value {
    font-size: 14px;
    color: #262626;
    font-weight: 600;
  }

  .value a {
    display: flex;
    align-items: center;
    color: #1890ff;
    text-decoration: none;
  }

  .value a:hover {
    color: #40a9ff;
  }
`;

export const ClientDetailsPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [client, setClient] = useState<ClientDetailDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);

  useEffect(() => {
    fetchClientDetails();
  }, [id]);

  const fetchClientDetails = async () => {
    try {
      setLoading(true);
      const response = await clientsApi.apiClientsIdGet({ id: Number(id) });
      setClient(response.data);
    } catch (err) {
      openNotificationWithIcon('error', t('clients:fetch-error', 'Failed to fetch client details'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (file: File) => {
    try {
      setLoading(true);

      await clientsApi.apiClientsClientIdDocumentsPost({
        clientId: Number(id),
        clientId2: Number(id),
        file,
        documentType: 'General',
      });
      openNotificationWithIcon('success', t('clients:doc-uploaded', 'Document uploaded successfully'));
      fetchClientDetails();
    } catch (err) {
      openNotificationWithIcon('error', t('clients:doc-upload-error', 'Failed to upload document'));
    } finally {
      setLoading(false);
    }
  };

  const downloadDocument = async (fileId: string, fileName: string) => {
    try {
      const response = await clientsApi.apiClientsClientIdDocumentsFileIdGet(
        {
          clientId: Number(id),
          fileId: fileId,
        },
        { responseType: 'blob' },
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName); // You might want to use actual filename here
      document.body.appendChild(link);
      link.click();
      link.remove(); // Clean up
      openNotificationWithIcon('success', t('clients:doc-downloaded', 'Document downloaded successfully'));
    } catch (error) {
      console.error(error);
      openNotificationWithIcon('error', t('clients:download-error', 'Failed to download document'));
    }
  };

  const handleDeleteDocument = async (docId: string) => {
    try {
      await clientsApi.apiClientsDocumentsIdDelete({ id: docId });
      openNotificationWithIcon('success', t('clients:doc-deleted', 'Document deleted successfully'));
      fetchClientDetails(); // Refresh the list
    } catch (err) {
      openNotificationWithIcon('error', t('clients:doc-delete-error', 'Failed to delete document'));
    }
  };

  const handleEditClick = () => {
    setModalVisible(true);
  };

  const handleModalCancel = () => {
    setModalVisible(false);
  };

  const handleModalSuccess = async () => {
    setModalVisible(false);
    await fetchClientDetails(); // Refresh client data
  };

  const statistics = [
    {
      title: t('clients:total-deals', 'Total Deals'),
      value: client?.statistics?.totalDeals || 0,
      icon: 'briefcase',
      color: '#1890ff',
    },
    {
      title: t('clients:active-deals', 'Active Deals'),
      value: client?.statistics?.activeDeals || 0,
      icon: 'check-circle',
      color: '#52c41a',
    },
    {
      title: t('clients:closed-deals', 'Closed Deals'),
      value: client?.statistics?.closedDeals || 0,
      icon: 'check-square',
      color: '#722ed1',
    },
    {
      title: t('clients:total-deal-amount', 'Total Deal Amount'),
      value: formatCurrency(client?.statistics?.totalDealAmount || 0),
      icon: 'dollar-sign',
      color: '#eb2f96',
    },
    {
      title: t('clients:total-paid', 'Total Paid'),
      value: formatCurrency(client?.statistics?.totalPaidAmount || 0),
      icon: 'check-circle',
      color: '#52c41a',
    },
    {
      title: t('clients:pending-amount', 'Pending Amount'),
      value: formatCurrency(client?.statistics?.totalPendingAmount || 0),
      icon: 'clock',
      color: '#faad14',
    },
    {
      title: t('clients:total-payments', 'Total Payments'),
      value: client?.statistics?.totalPayments || 0,
      icon: 'credit-card',
      color: '#13c2c2',
    },
    {
      title: t('clients:overdue-payments', 'Overdue Payments'),
      value: client?.statistics?.overduePayments || 0,
      icon: 'alert-circle',
      color: '#f5222d',
    },
  ];

  const cashFlowColumns = [
    {
      title: t('clients:project', 'Project'),
      key: 'project',
      render: (record: ClientCashFlowDto) => <Link to={`/projects/${record.projectId}`}>{record.projectName}</Link>,
    },
    {
      title: t('clients:amount', 'Amount'),
      dataIndex: 'expectedAmount',
      key: 'amount',
      render: (amount: number) => formatCurrency(amount),
    },
    {
      title: t('clients:due-date', 'Due Date'),
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: t('clients:status', 'Status'),
      key: 'status',
      render: (record: ClientCashFlowDto) => {
        return (
          <Tag color={getCashFlowStatusColor(record.status!)}>
            {t(
              `cashFlow.status.${getCashFlowStatusText(record.status!).key}`,
              getCashFlowStatusText(record.status!).title,
            )}
          </Tag>
        );
      },
    },
  ];

  return (
    <>
      <CardToolbox>
        <PageHeader
          ghost
          title={client?.name || client?.companyName}
          subTitle={client?.email}
          buttons={[
            hasPermission('clients:edit') && (
              <Button
                type="primary"
                size="small"
                icon={<FeatherIcon icon="edit" size={16} />}
                onClick={handleEditClick}
              >
                {t('global:edit', 'Edit')}
              </Button>
            ),
          ]}
        />
      </CardToolbox>

      <Main>
        {/* Statistics Section */}
        <Row gutter={[16, 16]}>
          {statistics.map((stat, index) => (
            <Col xs={24} sm={12} md={8} lg={6} key={index}>
              <Card bordered={false} loading={loading}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div
                    style={{
                      backgroundColor: `${stat.color}15`,
                      padding: '12px',
                      borderRadius: '8px',
                      marginRight: '12px',
                    }}
                  >
                    <FeatherIcon icon={stat.icon} size={20} style={{ color: stat.color }} />
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#8c8c8c' }}>{stat.title}</div>
                    <div style={{ fontSize: '18px', fontWeight: 'bold' }}>{stat.value}</div>
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        <Row gutter={[16, 16]} className="mt-4">
          <Col xs={24} md={16}>
            {/* Client Details Section */}
            <Card loading={loading} title={t('clients:details', 'Client Details')} className="mt-4">
              <Row gutter={[24, 16]}>
                <Col xs={24} sm={12} md={8}>
                  <ClientInfoItem>
                    <label>{t('clients:type', 'Client Type')}</label>
                    <div className="value">
                      {client?.type === ClientType.NUMBER_0
                        ? t('clients:individual', 'Individual')
                        : t('clients:company', 'Company')}
                    </div>
                  </ClientInfoItem>
                </Col>

                {client?.type === ClientType.NUMBER_0 ? (
                  <Col xs={24} sm={12} md={8}>
                    <ClientInfoItem>
                      <label>{t('clients:name', 'Name')}</label>
                      <div className="value">{client?.name}</div>
                    </ClientInfoItem>
                  </Col>
                ) : (
                  <>
                    <Col xs={24} sm={12} md={8}>
                      <ClientInfoItem>
                        <label>{t('clients:company-name', 'Company Name')}</label>
                        <div className="value">{client?.companyName}</div>
                      </ClientInfoItem>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <ClientInfoItem>
                        <label>{t('clients:registration-id', 'Registration ID')}</label>
                        <div className="value">{client?.companyRegistrationId || t('global:na', 'N/A')}</div>
                      </ClientInfoItem>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <ClientInfoItem>
                        <label>{t('clients:tax-number', 'Tax Number')}</label>
                        <div className="value">{client?.taxNumber || t('global:na', 'N/A')}</div>
                      </ClientInfoItem>
                    </Col>
                  </>
                )}

                <Col xs={24} sm={12} md={8}>
                  <ClientInfoItem>
                    <label>{t('clients:email', 'Email')}</label>
                    <div className="value">
                      {client?.email ? (
                        <a href={`mailto:${client.email}`}>
                          <FeatherIcon icon="mail" size={16} style={{ marginRight: 8 }} />
                          {client.email}
                        </a>
                      ) : (
                        t('global:na', 'N/A')
                      )}
                    </div>
                  </ClientInfoItem>
                </Col>

                <Col xs={24} sm={12} md={8}>
                  <ClientInfoItem>
                    <label>{t('clients:phone', 'Phone')}</label>
                    <div className="value">
                      {client?.phoneNumber ? (
                        <a href={`tel:${client.phoneNumber}`}>
                          <FeatherIcon icon="phone" size={16} style={{ marginRight: 8 }} />
                          {client.phoneNumber}
                        </a>
                      ) : (
                        t('global:na', 'N/A')
                      )}
                    </div>
                  </ClientInfoItem>
                </Col>

                <Col xs={24} sm={12} md={8}>
                  <ClientInfoItem>
                    <label>{t('clients:address', 'Address')}</label>
                    <div className="value">
                      {client?.address ? (
                        <>
                          <FeatherIcon icon="map-pin" size={16} style={{ marginRight: 8 }} />
                          {client.address}
                        </>
                      ) : (
                        t('global:na', 'N/A')
                      )}
                    </div>
                  </ClientInfoItem>
                </Col>
              </Row>
            </Card>
          </Col>

          {/* Documents Section */}
          <Col xs={24} md={8}>
            <Card
              title={t('clients:documents', 'Documents')}
              loading={loading}
              className="mt-4"
              extra={
                <Upload
                  showUploadList={false}
                  beforeUpload={file => {
                    handleUpload(file);
                    return false;
                  }}
                >
                  <Button type="primary" size="small" icon={<FeatherIcon icon="upload" size={16} />}>
                    {t('clients:upload', 'Upload')}
                  </Button>
                </Upload>
              }
            >
              {client?.documents && client.documents.length > 0 ? (
                <DocumentGrid>
                  {client.documents.map(doc => {
                    const { icon, color } = getFileIcon(doc.fileName || '');
                    return (
                      <DocumentItem key={doc.id}>
                        <div className="icon-wrapper">
                          <FeatherIcon icon={icon} size={24} style={{ color }} />
                        </div>
                        <div className="filename">{doc.fileName}</div>
                        <Space className="actions" size={2}>
                          <Button
                            type="text"
                            size="small"
                            icon={<FeatherIcon icon="download" size={14} />}
                            onClick={() => downloadDocument(doc.id!, doc.fileName || `document-${doc.id}`)}
                          />
                          <Button
                            type="text"
                            size="small"
                            danger
                            icon={<FeatherIcon icon="trash-2" size={14} />}
                            onClick={async () => {
                              await handleDeleteDocument(doc.id!);
                            }}
                          />
                        </Space>
                      </DocumentItem>
                    );
                  })}
                </DocumentGrid>
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={t('clients:no-documents-added', 'No documents added yet')}
                />
              )}
            </Card>
          </Col>
        </Row>

        {/* Payments Section */}
        <Row gutter={[16, 16]} className="mt-4">
          <Col xs={24} md={24}>
            <Card title={t('clients:payments', 'Payments')} loading={loading}>
              <CustomTableStyleWrapper>
                <TableWrapper>
                  <Table
                    dataSource={client?.cashFlows || []}
                    columns={cashFlowColumns}
                    rowKey="id"
                    pagination={{ pageSize: 10 }}
                  />{' '}
                </TableWrapper>
              </CustomTableStyleWrapper>
            </Card>
          </Col>
        </Row>
      </Main>

      {modalVisible && (
        <ClientForm
          visible={modalVisible}
          editMode={true}
          client={client}
          onCancel={handleModalCancel}
          onSuccess={handleModalSuccess}
        />
      )}
    </>
  );
};

export default ClientDetailsPage;
